import React, { useState } from 'react';
import { FeeStatementRow } from '../types';

interface FeeStatementTableProps {
  title: string;
  data: FeeStatementRow[];
  colorScheme: 'blue' | 'green';
  selectedRowIndex?: number;
  onRowSelect?: (index: number | null) => void;
  onExport?: () => void | Promise<void>;
  isExporting?: boolean;
}

const FeeStatementTable: React.FC<FeeStatementTableProps> = ({
  title,
  data,
  colorScheme,
  selectedRowIndex,
  onRowSelect,
  onExport,
  isExporting = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showDescription, setShowDescription] = useState(false);
  const [showAllRows, setShowAllRows] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  };

  // Find the index of the last credit row
  const getLastCreditIndex = () => {
    for (let i = data.length - 1; i >= 0; i--) {
      if (data[i].credit > 0) {
        return i;
      }
    }
    return -1;
  };

  // Check if a row is selectable
  const isRowSelectable = (index: number) => {
    const lastCreditIndex = getLastCreditIndex();
    const row = data[index];
    // Row is selectable if it's after the last credit and has running balance > 0 and balance type is not 'Cr'
    return index > lastCreditIndex && row.running_balance > 0 && row.balance_type !== 'Cr';
  };

  // Get filtered data based on showAllRows
  const getFilteredData = () => {
    if (showAllRows) {
      return data;
    }
    // Show only selectable rows
    return data.filter((_, index) => isRowSelectable(index));
  };

  const handleRowClick = (index: number) => {
    if (!onRowSelect) return;
    
    if (isRowSelectable(index)) {
      // Toggle selection
      if (selectedRowIndex === index) {
        onRowSelect(null); // Deselect
      } else {
        onRowSelect(index); // Select this row
      }
    }
  };

  const colors = {
    blue: {
      header: 'bg-blue-600',
      headerHover: 'hover:bg-blue-700',
      border: 'border-blue-200',
      rowHover: 'hover:bg-blue-50',
      text: 'text-blue-600',
    },
    green: {
      header: 'bg-green-600',
      headerHover: 'hover:bg-green-700',
      border: 'border-green-200',
      rowHover: 'hover:bg-green-50',
      text: 'text-green-600',
    },
  };

  const scheme = colors[colorScheme];
  const filteredData = getFilteredData();

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
      {/* Header with Expand/Collapse */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full ${scheme.header} ${scheme.headerHover} text-white px-4 md:px-6 py-4 transition-colors duration-200`}
      >
        {/* Mobile Layout */}
        <div className="md:hidden">
          {/* First Row: Title and Arrow */}
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-bold">{title}</h3>
            <svg
              className={`w-6 h-6 transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
          
          {/* Second Row: Transaction Count and Buttons */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded-full whitespace-nowrap">
              {filteredData.length} {filteredData.length === 1 ? 'Transaction' : 'Transactions'}
            </span>
            
            <div className="flex items-center gap-1 flex-wrap justify-end">
              {isExpanded && onRowSelect && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDescription(!showDescription);
                    }}
                    className="text-xs bg-white bg-opacity-30 hover:bg-opacity-40 px-2 py-1 rounded transition-colors whitespace-nowrap"
                  >
                    {showDescription ? 'Hide Desc' : 'Show Desc'}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAllRows(!showAllRows);
                    }}
                    className="text-xs bg-white bg-opacity-30 hover:bg-opacity-40 px-2 py-1 rounded transition-colors whitespace-nowrap"
                  >
                    {showAllRows ? 'Hide All' : 'Show All'}
                  </button>
                </>
              )}
              {onExport && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onExport();
                  }}
                  disabled={isExporting}
                  className="text-xs bg-white hover:bg-opacity-90 bg-opacity-80 text-gray-800 font-semibold px-2 py-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 whitespace-nowrap"
                >
                  {isExporting ? (
                    <>
                      <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span className="hidden sm:inline">Exporting...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2m0-8v6m0 0l-3-3m3 3l3-3" />
                      </svg>
                      <span>PDF</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold">{title}</h3>
          </div>
          <div className="flex items-center gap-3">
            {isExpanded && onRowSelect && (
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDescription(!showDescription);
                  }}
                  className="text-xs bg-white bg-opacity-30 hover:bg-opacity-40 px-3 py-1 rounded transition-colors"
                >
                  {showDescription ? 'Hide Description' : 'Show Description'}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAllRows(!showAllRows);
                  }}
                  className="text-xs bg-white bg-opacity-30 hover:bg-opacity-40 px-3 py-1 rounded transition-colors"
                >
                  {showAllRows ? 'Hide All' : 'Show All'}
                </button>
              </div>
            )}
            {onExport && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onExport();
                }}
                disabled={isExporting}
                className="text-xs bg-white hover:bg-opacity-90 bg-opacity-80 text-gray-800 font-semibold px-4 py-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {isExporting ? (
                  <>
                    <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>Exporting...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2m0-8v6m0 0l-3-3m3 3l3-3" />
                    </svg>
                    <span>Export PDF</span>
                  </>
                )}
              </button>
            )}
            <span className="text-sm bg-white bg-opacity-20 px-3 py-1 rounded-full">
              {filteredData.length} {filteredData.length === 1 ? 'Transaction' : 'Transactions'}
            </span>
            <svg
              className={`w-6 h-6 transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </button>

      {/* Mobile List View */}
      {isExpanded && (
        <div className="md:hidden">
          <div className="divide-y divide-gray-200">
            {filteredData.map((row, filteredIndex) => {
              const originalIndex = data.indexOf(row);
              const selectable = isRowSelectable(originalIndex);
              const isSelected = selectedRowIndex === originalIndex;
              return (
                <div
                  key={filteredIndex}
                  onClick={() => handleRowClick(originalIndex)}
                  className={`p-4 transition-colors duration-150 ${
                    isSelected
                      ? `${scheme.header} text-white`
                      : selectable
                      ? `${scheme.rowHover} cursor-pointer`
                      : 'bg-gray-50 opacity-60'
                  }`}
                >
                  {/* Selection and Date */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {selectable && (
                        <input
                          type="radio"
                          checked={isSelected}
                          onChange={() => {}}
                          className="w-4 h-4"
                        />
                      )}
                      <span className={`text-sm font-semibold ${isSelected ? 'text-white' : 'text-gray-700'}`}>
                        {formatDate(row.ddate)}
                      </span>
                    </div>
                    <span className={`text-xs ${isSelected ? 'text-white opacity-80' : 'text-gray-500'}`}>
                      Ref: {row.ref || '-'}
                    </span>
                  </div>

                  {/* Description (if shown) */}
                  {showDescription && row.description && (
                    <div className={`text-sm mb-2 ${isSelected ? 'text-white' : 'text-gray-700'}`}>
                      {row.description}
                    </div>
                  )}

                  {/* Amounts Grid */}
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <div className={`text-xs uppercase ${isSelected ? 'text-white opacity-70' : 'text-gray-500'}`}>
                        Charged
                      </div>
                      <div className="text-sm font-semibold">
                        {row.charged > 0 ? (
                          <span className={isSelected ? 'text-white' : 'text-red-600'}>
                            {formatCurrency(row.charged)}
                          </span>
                        ) : (
                          <span className={isSelected ? 'text-white opacity-60' : 'text-gray-400'}>-</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className={`text-xs uppercase ${isSelected ? 'text-white opacity-70' : 'text-gray-500'}`}>
                        Credit
                      </div>
                      <div className="text-sm font-semibold">
                        {row.credit > 0 ? (
                          <span className={isSelected ? 'text-white' : 'text-green-600'}>
                            {formatCurrency(row.credit)}
                          </span>
                        ) : (
                          <span className={isSelected ? 'text-white opacity-60' : 'text-gray-400'}>-</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Running Balance */}
                  <div className="pt-2 border-t border-opacity-20" style={{ borderColor: isSelected ? 'white' : '#e5e7eb' }}>
                    <div className={`text-xs uppercase mb-1 ${isSelected ? 'text-white opacity-70' : 'text-gray-500'}`}>
                      Running Balance
                    </div>
                    <div className="flex items-center justify-between">
                      <span
                        className={`text-base font-bold ${
                          isSelected
                            ? 'text-white'
                            : row.balance_type === 'Dr'
                            ? 'text-red-600'
                            : 'text-green-600'
                        }`}
                      >
                        {formatCurrency(row.running_balance)}
                      </span>
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded ${
                          isSelected
                            ? 'bg-white bg-opacity-20 text-white'
                            : row.balance_type === 'Dr'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {row.balance_type}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile Totals Footer */}
          <div className="bg-gray-100 p-4 border-t-2 border-gray-300">
            <div className="font-bold text-gray-700 mb-3 text-sm uppercase">Total</div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <div className="text-xs text-gray-500 uppercase">Charged</div>
                <div className="text-sm font-bold text-red-600">
                  {formatCurrency(data.reduce((sum, row) => sum + row.charged, 0))}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase">Credit</div>
                <div className="text-sm font-bold text-green-600">
                  {formatCurrency(data.reduce((sum, row) => sum + row.credit, 0))}
                </div>
              </div>
            </div>
            {data.length > 0 && (
              <div className="pt-3 border-t border-gray-300">
                <div className="text-xs text-gray-500 uppercase mb-1">Final Balance</div>
                <div className="flex items-center justify-between">
                  <span
                    className={`text-base font-bold ${
                      data[data.length - 1].balance_type === 'Dr'
                        ? 'text-red-600'
                        : 'text-green-600'
                    }`}
                  >
                    {formatCurrency(data[data.length - 1].running_balance)}
                  </span>
                  <span
                    className={`text-xs font-semibold px-2 py-1 rounded ${
                      data[data.length - 1].balance_type === 'Dr'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-100 text-green-700'
                    }`}
                  >
                    {data[data.length - 1].balance_type}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Desktop Table View */}
      {isExpanded && (
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full table-fixed">
            <colgroup>
              {showDescription ? (
                <>
                  <col className="w-[15%]" />
                  <col className="w-[8%]" />
                  <col className="w-[22%]" />
                  <col className="w-[13%]" />
                  <col className="w-[13%]" />
                  <col className="w-[19%]" />
                </>
              ) : (
                <>
                  <col className="w-[18%]" />
                  <col className="w-[10%]" />
                  <col className="w-[14%]" />
                  <col className="w-[14%]" />
                  <col className="w-[20%]" />
                </>
              )}
            </colgroup>
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-2 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-2 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Ref
                </th>
                {showDescription && (
                  <th className="px-2 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Description
                  </th>
                )}
                <th className="px-2 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Charged
                </th>
                <th className="px-2 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Credit
                </th>
                <th className="px-2 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Running Balance
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredData.map((row, filteredIndex) => {
                // Find the original index in the full data array
                const originalIndex = data.indexOf(row);
                const selectable = isRowSelectable(originalIndex);
                const isSelected = selectedRowIndex === originalIndex;
                return (
                  <tr
                    key={filteredIndex}
                    onClick={() => handleRowClick(originalIndex)}
                    className={`transition-colors duration-150 ${
                      isSelected
                        ? `${scheme.header} text-white`
                        : selectable
                        ? `${scheme.rowHover} cursor-pointer`
                        : 'bg-gray-50 opacity-60'
                    }`}
                  >
                    <td className={`px-2 py-3 text-sm whitespace-nowrap ${
                      isSelected ? 'text-white' : 'text-gray-700'
                    }`}>
                      <div className="flex items-center gap-2">
                        {selectable && (
                          <input
                            type="radio"
                            checked={isSelected}
                            onChange={() => {}}
                            className="w-4 h-4"
                          />
                        )}
                        {formatDate(row.ddate)}
                      </div>
                    </td>
                    <td className={`px-2 py-3 text-sm whitespace-nowrap ${
                      isSelected ? 'text-white' : 'text-gray-700'
                    }`}>
                      {row.ref || '-'}
                    </td>
                    {showDescription && (
                      <td className={`px-2 py-3 text-sm ${
                        isSelected ? 'text-white' : 'text-gray-700'
                      }`}>
                        <div className="truncate" title={row.description || '-'}>
                          {row.description || '-'}
                        </div>
                      </td>
                    )}
                    <td className="px-2 py-3 text-sm text-right font-semibold">
                      {row.charged > 0 ? (
                        <span className={isSelected ? 'text-white' : 'text-red-600'}>
                          {formatCurrency(row.charged)}
                        </span>
                      ) : (
                        <span className={isSelected ? 'text-white opacity-60' : 'text-gray-400'}>-</span>
                      )}
                    </td>
                    <td className="px-2 py-3 text-sm text-right font-semibold">
                      {row.credit > 0 ? (
                        <span className={isSelected ? 'text-white' : 'text-green-600'}>
                          {formatCurrency(row.credit)}
                        </span>
                      ) : (
                        <span className={isSelected ? 'text-white opacity-60' : 'text-gray-400'}>-</span>
                      )}
                    </td>
                    <td className="px-2 py-3 text-sm text-right font-bold">
                      <div className="flex items-center justify-end gap-2">
                        <span
                          className={
                            isSelected
                              ? 'text-white'
                              : row.balance_type === 'Dr'
                              ? 'text-red-600'
                              : 'text-green-600'
                          }
                        >
                          {formatCurrency(row.running_balance)}
                        </span>
                        <span
                          className={`text-xs font-semibold px-2 py-1 rounded ${
                            isSelected
                              ? 'bg-white bg-opacity-20 text-white'
                              : row.balance_type === 'Dr'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-green-100 text-green-700'
                          }`}
                        >
                          {row.balance_type}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {/* Footer with Totals */}
            <tfoot className="bg-gray-100 border-t-2 border-gray-300">
              <tr>
                <td colSpan={showDescription ? 3 : 2} className="px-2 py-3 text-sm font-bold text-gray-700">
                  Total
                </td>
                <td className="px-2 py-3 text-sm text-right font-bold text-red-600">
                  {formatCurrency(
                    data.reduce((sum, row) => sum + row.charged, 0)
                  )}
                </td>
                <td className="px-2 py-3 text-sm text-right font-bold text-green-600">
                  {formatCurrency(
                    data.reduce((sum, row) => sum + row.credit, 0)
                  )}
                </td>
                <td className="px-2 py-3 text-sm text-right font-bold">
                  {data.length > 0 && (
                    <div className="flex items-center justify-end gap-2">
                      <span
                        className={
                          data[data.length - 1].balance_type === 'Dr'
                            ? 'text-red-600'
                            : 'text-green-600'
                        }
                      >
                        {formatCurrency(data[data.length - 1].running_balance)}
                      </span>
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded ${
                          data[data.length - 1].balance_type === 'Dr'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {data[data.length - 1].balance_type}
                      </span>
                    </div>
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default FeeStatementTable;
